import { parseProject, type Project } from '@finsim/model';
import { combine, createEffect, createEvent, createStore, merge, sample } from 'effector';
import { debounce } from 'patronum';
import type { ProjectMeta } from '../../shared/api/local-projects';
import { clearPending, readPending, writePending } from '../../shared/api/pending-save';
import { $project, editCommitted, projectClosed, projectLoaded, redo, undo } from '../project';
import { downloadProject } from './remote-copy';
import { local } from './store';

/*
 * The user's projects (local-first, IndexedDB) and autosave of the open project.
 */

export const AUTOSAVE_MS = 300;

export class ProjectLoadError extends Error {
  constructor(readonly code: 'notFound' | 'invalid' | 'futureVersion') {
    super(code);
  }
}

export const listProjectsFx = createEffect(() => local().list());

export const loadProjectFx = createEffect(async (id: string): Promise<Project> => {
  // An unload-time snapshot is newer than anything in IndexedDB.
  const pending = readPending(id);
  if (pending !== undefined) {
    const parsed = parseProject(pending);
    if (parsed.success) {
      await local().save(parsed.data, Date.now());
      // An edit that never reached storage: if the project syncs, it still has to be uploaded.
      const sync = await local().getSync(id);
      if (sync?.ownerId) await local().setSync(id, { ...sync, dirty: true });
      clearPending(id);
      return parsed.data;
    }
    clearPending(id);
  }
  const raw = await local().load(id);
  if (raw === undefined) {
    const downloaded = await downloadProject(id);
    if (downloaded) return downloaded;
    throw new ProjectLoadError('notFound');
  }
  const parsed = parseProject(raw);
  if (!parsed.success)
    throw new ProjectLoadError(parsed.error === 'futureVersion' ? 'futureVersion' : 'invalid');
  return parsed.data;
});

export const saveProjectFx = createEffect(async (project: Project) => {
  const index = await local().save(project, Date.now());
  clearPending(project.id);
  return index;
});

/** Synchronous snapshot for page unload (see shared/api/pending-save). */
export const writePendingFx = createEffect((project: Project) => {
  writePending(project);
});
export const deleteProjectFx = createEffect((id: string) => local().remove(id));

export const libraryRequested = createEvent();
/** Save now if there are unsaved changes (e.g. on page hide). */
export const flushRequested = createEvent();

export const $library = createStore<ProjectMeta[]>([])
  .on(listProjectsFx.doneData, (_, list) => list)
  .on(saveProjectFx.doneData, (_, list) => list)
  .on(deleteProjectFx.doneData, (_, list) => list);

const changed = merge([editCommitted, undo, redo]);

/** The project has edits not yet written to storage. */
export const $dirty = createStore(false)
  .on(changed, () => true)
  .reset(projectLoaded, projectClosed);

export const $saveError = createStore(false)
  .on(saveProjectFx.fail, () => true)
  .reset(saveProjectFx.done, projectLoaded);

export type SaveStatus = 'saved' | 'unsaved' | 'saving' | 'error';
export const $saveStatus = combine(
  $dirty,
  saveProjectFx.pending,
  $saveError,
  (dirty, saving, error): SaveStatus =>
    saving ? 'saving' : error ? 'error' : dirty ? 'unsaved' : 'saved',
);

sample({ clock: libraryRequested, target: listProjectsFx });

const unsaved = { project: $project, dirty: $dirty };
const isUnsaved = ({ project, dirty }: { project: Project | null; dirty: boolean }): boolean =>
  project !== null && dirty;

sample({
  clock: debounce(changed, AUTOSAVE_MS),
  source: unsaved,
  filter: isUnsaved,
  fn: ({ project }) => project as Project,
  target: saveProjectFx,
});
// On page hide: snapshot synchronously first, then attempt the normal save.
sample({
  clock: flushRequested,
  source: unsaved,
  filter: isUnsaved,
  fn: ({ project }) => project as Project,
  target: [writePendingFx, saveProjectFx],
});

// Clean only if nothing changed while saving.
sample({
  clock: saveProjectFx.done,
  source: $project,
  filter: (current, { params }) => current === params,
  fn: () => false,
  target: $dirty,
});
